import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchCartJsonShared } from '@theme/utilities';

/** @type {Set<HTMLButtonElement>} */
const loadingButtons = new Set();

/** @type {ProductAddonsElement[]} */
const addonComponents = [];

let processingLock = false;
let cartInterceptAttached = false;

/**
 * @returns {ProductAddonsElement[]}
 */
function getAllAddonComponents() {
  return addonComponents;
}

/**
 * @returns {{ variantId: string, quantity: number }[]}
 */
function getAllSelectedVariants() {
  const combined = [];
  for (const component of getAllAddonComponents()) {
    combined.push(...component.selectedVariants);
  }
  return combined;
}

/**
 * @returns {string}
 */
function getCartSectionIds() {
  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  }
  return [...new Set(sectionIds)].join(',');
}

/**
 * @param {HTMLFormElement} form
 * @returns {{ id: number, quantity: number }[]}
 */
function buildBundledCartItems(form) {
  const formData = new FormData(form);
  const mainVariantId = Number.parseInt(String(formData.get('id') || ''), 10);
  const mainQuantity = Number.parseInt(String(formData.get('quantity') || '1'), 10) || 1;

  if (!Number.isFinite(mainVariantId) || mainVariantId <= 0) {
    throw new Error('Missing or invalid main product variant');
  }

  const addonLines = getAllSelectedVariants()
    .map((line) => ({
      id: Number.parseInt(String(line.variantId), 10),
      quantity: Number.parseInt(String(line.quantity), 10) || 1,
    }))
    .filter((line) => Number.isFinite(line.id) && line.id > 0 && line.quantity > 0);

  return [{ id: mainVariantId, quantity: mainQuantity }, ...addonLines];
}

/**
 * @param {Record<string, unknown>} response
 * @returns {number}
 */
function getAddedItemCount(response) {
  if (Array.isArray(response.items)) {
    return response.items.reduce(
      (sum, item) => sum + (Number(/** @type {{ quantity?: number }} */ (item).quantity) || 0),
      0
    );
  }
  return 0;
}

/**
 * @param {Record<string, unknown>} response
 * @param {number} fallbackCount
 */
async function notifyCartAddSuccess(response, fallbackCount) {
  const cart = await fetchCartJsonShared();
  document.dispatchEvent(
    new CartAddEvent(cart, 'product-addons', {
      source: 'product-addons',
      itemCount: cart.item_count ?? fallbackCount,
      sections: /** @type {Record<string, string>} */ (response.sections) ?? {},
    })
  );
}

/**
 * @param {string} message
 */
function notifyCartAddError(message) {
  document.dispatchEvent(
    new CartErrorEvent('product-addons', message, message, {})
  );
}

/**
 * @param {HTMLFormElement | null} form
 * @param {HTMLElement | null} triggerButton
 */
async function handleBundledAddToCart(form, triggerButton) {
  if (!form || processingLock) return;

  processingLock = true;
  setLoadingState(triggerButton, true);

  try {
    const items = buildBundledCartItems(form);
    const sections = getCartSectionIds();
    const payload = sections ? { items, sections } : { items };

    const response = await fetch(
      typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }
    );

    const responseBody = await response.json();

    if (!response.ok || responseBody.status) {
      const message = responseBody.description || responseBody.message || 'Unable to add items to cart';
      notifyCartAddError(message);
      throw new Error(message);
    }

    clearAllSelections();
    await notifyCartAddSuccess(responseBody, getAddedItemCount(responseBody));
  } catch (error) {
    console.error('[product-addons] Add to cart failed', error);
  } finally {
    setLoadingState(triggerButton, false);
    setTimeout(() => {
      processingLock = false;
    }, 300);
  }
}

/**
 * @param {HTMLFormElement | null} form
 * @param {HTMLElement | null} triggerButton
 */
async function handleBundledBuyNow(form, triggerButton) {
  if (!form || processingLock) return;

  processingLock = true;
  setLoadingState(triggerButton, true);

  try {
    const items = buildBundledCartItems(form);
    const response = await fetch(
      typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      }
    );

    const responseBody = await response.json();

    if (!response.ok || responseBody.status) {
      const message = responseBody.description || responseBody.message || 'Unable to add items to cart';
      notifyCartAddError(message);
      throw new Error(message);
    }

    clearAllSelections();
    setLoadingState(triggerButton, false);
    setTimeout(() => {
      window.location.href = '/checkout';
    }, 150);
  } catch (error) {
    console.error('[product-addons] Buy now cart add failed', error);
    setLoadingState(triggerButton, false);
  } finally {
    setTimeout(() => {
      processingLock = false;
    }, 300);
  }
}

/**
 * @param {HTMLElement | null} triggerButton
 * @param {boolean} isLoading
 */
function setLoadingState(triggerButton, isLoading) {
  for (const component of getAllAddonComponents()) {
    component.classList.toggle('product-addons--loading', isLoading);
  }

  if (!triggerButton) return;

  if (isLoading) {
    loadingButtons.add(/** @type {HTMLButtonElement} */ (triggerButton));
    triggerButton.classList.add('loading-btn');
    if (triggerButton instanceof HTMLButtonElement) {
      triggerButton.disabled = true;
    }
  } else {
    loadingButtons.forEach((button) => {
      button.classList.remove('loading-btn');
      button.disabled = false;
    });
    loadingButtons.clear();
  }
}

function clearAllSelections() {
  for (const component of getAllAddonComponents()) {
    component.clearSelections();
  }
}

/**
 * @param {Event} event
 */
function handleDocumentClick(event) {
  if (processingLock || getAllSelectedVariants().length === 0) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const productFormComponent = target.closest('product-form-component');
  if (!productFormComponent) return;

  const form = productFormComponent.querySelector('form[action*="/cart/add"]');
  if (!(form instanceof HTMLFormElement)) return;

  const isDynamicCheckout =
    target.closest('.shopify-payment-button') || target.closest('[data-shopify="payment-button"]');
  const submitButton = target.closest('button[type="submit"], input[type="submit"]');

  if (isDynamicCheckout) {
    event.preventDefault();
    event.stopPropagation();
    handleBundledBuyNow(form, target.closest('button') || target);
    return;
  }

  if (submitButton && productFormComponent.contains(submitButton)) {
    event.preventDefault();
    event.stopPropagation();
    handleBundledAddToCart(form, submitButton);
  }
}

/**
 * @param {Event} event
 */
function handleDocumentSubmit(event) {
  if (processingLock || getAllSelectedVariants().length === 0) return;

  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.closest('product-form-component')) return;

  event.preventDefault();
  event.stopPropagation();
  const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
  handleBundledAddToCart(form, submitButton);
}

function attachCartIntercept() {
  if (cartInterceptAttached) return;
  cartInterceptAttached = true;
  document.addEventListener('click', handleDocumentClick, true);
  document.addEventListener('submit', handleDocumentSubmit, true);
}

/**
 * @param {HTMLElement} card
 * @returns {boolean}
 */
function isUnavailableCard(card) {
  return card.getAttribute('data-available') === 'false';
}

class ProductAddonsElement extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  constructor() {
    super();
    /** @type {{ variantId: string, quantity: number }[]} */
    this.selectedVariants = [];
    this.isSingleSelection = false;
    this.isModernLayout = false;
    this.showQuantity = false;
  }

  connectedCallback() {
    this.isSingleSelection = this.dataset.singleSelection === 'true';
    this.isModernLayout = this.dataset.layout === 'modern';
    this.showQuantity = this.dataset.showQuantity === 'true';
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (!addonComponents.includes(this)) {
      addonComponents.push(this);
    }

    attachCartIntercept();

    if (this.isModernLayout) {
      this.#setupModernPillListeners();
    } else {
      this.#setupCardListeners();
    }

    this.#setupVariantSelects();
    this.#setupQuantityControls();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    const index = addonComponents.indexOf(this);
    if (index > -1) {
      addonComponents.splice(index, 1);
    }
  }

  clearSelections() {
    for (const input of this.querySelectorAll('.product-addon__checkbox')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = false;
      const card = input.closest('.product-addon-card');
      if (!(card instanceof HTMLElement)) continue;
      card.classList.remove('selected');
      const quantitySection = card.querySelector('.product-addon__quantity-section');
      if (quantitySection instanceof HTMLElement) {
        quantitySection.classList.remove('visible');
        quantitySection.hidden = true;
        const quantityInput = quantitySection.querySelector('.product-addon__quantity-display');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = '1';
        }
      }
      if (this.isModernLayout) {
        this.#syncModernPill(card, false);
      }
    }
    this.selectedVariants = [];
  }

  updateSelectedVariants() {
    const selected = [];

    for (const input of this.querySelectorAll('.product-addon__checkbox')) {
      if (!(input instanceof HTMLInputElement) || !input.checked || input.disabled) continue;

      const card = input.closest('.product-addon-card');
      if (!(card instanceof HTMLElement)) continue;

      const quantityInput = card.querySelector('.product-addon__quantity-display');
      const parsedQuantity =
        quantityInput instanceof HTMLInputElement ? Number.parseInt(quantityInput.value, 10) : 1;
      const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;

      if (input.dataset.variantId) {
        selected.push({ variantId: input.dataset.variantId, quantity });
      }
    }

    this.selectedVariants = selected;
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} isSelected
   */
  #syncModernPill(card, isSelected) {
    const pill = card.querySelector('.product-addon__pill');
    const toggle = card.querySelector('.product-addon__pill-toggle');
    const label = card.querySelector('.product-addon__pill-label');
    const quantitySection = card.querySelector('.product-addon__quantity-section');
    const isUnavailable = isUnavailableCard(card);

    if (!(pill instanceof HTMLElement)) return;

    const labelAdd = pill.dataset.labelAdd || 'ADD';
    const labelAdded = pill.dataset.labelAdded || 'ADDED';
    const labelSoldOut = pill.dataset.labelSoldOut || 'SOLD OUT';

    if (label instanceof HTMLElement) {
      if (isUnavailable) {
        label.textContent = labelSoldOut;
      } else {
        label.textContent = isSelected ? labelAdded : labelAdd;
      }
    }

    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      toggle.setAttribute('aria-label', isUnavailable ? labelSoldOut : isSelected ? labelAdded : labelAdd);
      toggle.disabled = isUnavailable;
    }

    if (quantitySection instanceof HTMLElement) {
      const showQty = isSelected && card.dataset.qtyMode === 'true' && !isUnavailable;
      quantitySection.hidden = !showQty;
      quantitySection.classList.toggle('visible', showQty);
      if (!isSelected) {
        const qtyInput = quantitySection.querySelector('.product-addon__quantity-display');
        if (qtyInput instanceof HTMLInputElement) {
          qtyInput.value = '1';
        }
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLInputElement} checkbox
   */
  #handleCheckboxChange(card, checkbox) {
    if (this.isSingleSelection && checkbox.checked) {
      for (const otherCheckbox of this.querySelectorAll('.product-addon__checkbox')) {
        if (!(otherCheckbox instanceof HTMLInputElement) || otherCheckbox === checkbox) continue;
        otherCheckbox.checked = false;
        const otherCard = otherCheckbox.closest('.product-addon-card');
        if (!(otherCard instanceof HTMLElement)) continue;
        otherCard.classList.remove('selected');
        const otherQty = otherCard.querySelector('.product-addon__quantity-section');
        if (otherQty instanceof HTMLElement) {
          otherQty.classList.remove('visible');
          otherQty.hidden = true;
        }
        if (this.isModernLayout) {
          this.#syncModernPill(otherCard, false);
        }
      }
    }

    card.classList.toggle('selected', checkbox.checked);

    if (this.isModernLayout) {
      this.#syncModernPill(card, checkbox.checked);
    } else {
      const quantitySection = card.querySelector('.product-addon__quantity-section');
      if (quantitySection instanceof HTMLElement) {
        quantitySection.classList.toggle('visible', checkbox.checked);
        quantitySection.hidden = !checkbox.checked;
        if (!checkbox.checked) {
          const qtyInput = quantitySection.querySelector('.product-addon__quantity-display');
          if (qtyInput instanceof HTMLInputElement) {
            qtyInput.value = '1';
          }
        }
      }
    }

    this.updateSelectedVariants();
  }

  #setupModernPillListeners() {
    const { signal } = this.#abortController ?? {};

    for (const card of this.querySelectorAll('.product-addon-card--modern')) {
      if (!(card instanceof HTMLElement)) continue;

      const checkbox = card.querySelector('.product-addon__checkbox');
      const toggle = card.querySelector('[data-action="toggle-addon"]');
      if (!(checkbox instanceof HTMLInputElement) || !(toggle instanceof HTMLButtonElement)) continue;

      this.#syncModernPill(card, checkbox.checked);

      toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (isUnavailableCard(card) || checkbox.disabled) return;

          checkbox.checked = !checkbox.checked;
          this.#handleCheckboxChange(card, checkbox);
        },
        { signal }
      );
    }
  }

  #setupCardListeners() {
    const { signal } = this.#abortController ?? {};

    for (const card of this.querySelectorAll('.product-addon-card')) {
      if (!(card instanceof HTMLElement)) continue;

      const checkbox = card.querySelector('.product-addon__checkbox');
      if (!(checkbox instanceof HTMLInputElement)) continue;

      checkbox.addEventListener(
        'change',
        () => {
          this.#handleCheckboxChange(card, checkbox);
        },
        { signal }
      );

      card.addEventListener(
        'click',
        (event) => {
          if (isUnavailableCard(card)) return;
          if (!(event.target instanceof Element)) return;
          if (
            event.target.closest('.product-addon__quantity-controls') ||
            event.target.closest('.product-addon__link-btn') ||
            event.target.closest('.product-addon__variant-select') ||
            event.target.closest('.product-addon__checkbox-wrapper')
          ) {
            return;
          }

          if (checkbox.disabled) return;

          checkbox.checked = !checkbox.checked;
          this.#handleCheckboxChange(card, checkbox);
        },
        { signal }
      );
    }
  }

  #setupVariantSelects() {
    const { signal } = this.#abortController ?? {};

    for (const select of this.querySelectorAll('.product-addon__variant-select')) {
      if (!(select instanceof HTMLSelectElement)) continue;

      select.addEventListener(
        'change',
        () => {
          const card = select.closest('.product-addon-card');
          if (!(card instanceof HTMLElement)) return;

          const option = select.selectedOptions[0];
          if (!option) return;

          this.#applyVariantOptionToCard(card, option.value);
          this.updateSelectedVariants();
        },
        { signal }
      );

      select.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
        },
        { signal }
      );
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {Record<string, { priceFormatted?: string, compareAtPriceFormatted?: string | null, variantTitle?: string, imageUrl?: string, imageAlt?: string, available?: boolean }>}
   */
  #getVariantMap(card) {
    const dataEl = card.querySelector('.product-addon__variant-data');
    if (!(dataEl instanceof HTMLScriptElement)) return {};

    try {
      const parsed = JSON.parse(dataEl.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string} variantId
   */
  #updateProductLinks(card, variantId) {
    const productUrl = card.dataset.productUrl;
    if (!productUrl || !variantId) return;

    let href;
    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      href = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      href = `${productUrl}${productUrl.includes('?') ? '&' : '?'}variant=${variantId}`;
    }

    for (const link of card.querySelectorAll(
      '.product-addon__media-link, a.product-addon__product-title, a.product-addon__link-btn'
    )) {
      if (link instanceof HTMLAnchorElement) {
        link.href = href;
      }
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {string} variantId
   */
  #applyVariantOptionToCard(card, variantId) {
    const variantData = this.#getVariantMap(card)[variantId] || {};
    const isAvailable = variantData.available !== false;

    card.dataset.variantId = variantId;
    card.dataset.available = isAvailable ? 'true' : 'false';
    this.#updateProductLinks(card, variantId);

    const checkbox = card.querySelector('.product-addon__checkbox');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.dataset.variantId = variantId;
      checkbox.dataset.variantTitle = variantData.variantTitle || '';
      checkbox.disabled = !isAvailable;
    }

    const priceEl = card.querySelector('.product-addon__price');
    if (priceEl instanceof HTMLElement && variantData.priceFormatted) {
      let currentEl = priceEl.querySelector('.product-addon__price-current');
      if (!(currentEl instanceof HTMLElement)) {
        priceEl.replaceChildren();
        currentEl = document.createElement('span');
        currentEl.className = 'product-addon__price-current';
        priceEl.appendChild(currentEl);
      }
      currentEl.textContent = variantData.priceFormatted;

      let compareEl = priceEl.querySelector('.product-addon__price-compare');
      if (variantData.compareAtPriceFormatted) {
        if (!(compareEl instanceof HTMLElement)) {
          compareEl = document.createElement('s');
          compareEl.className = 'product-addon__price-compare compare-at-price';
          priceEl.appendChild(compareEl);
        }
        compareEl.textContent = variantData.compareAtPriceFormatted;
      } else if (compareEl) {
        compareEl.remove();
      }
    }

    const quantitySection = card.querySelector('.product-addon__quantity-section');
    if (quantitySection instanceof HTMLElement) {
      quantitySection.dataset.variantId = variantId;
    }

    let imageEl = card.querySelector('.product-addon__image');
    if (variantData.imageUrl) {
      if (!(imageEl instanceof HTMLImageElement)) {
        const imageWrapper = card.querySelector('.product-addon__image-wrapper');
        const placeholder = imageWrapper?.querySelector('.product-addon__image-placeholder');
        if (imageWrapper instanceof HTMLElement) {
          imageEl = document.createElement('img');
          imageEl.className = 'product-addon__image';
          imageEl.loading = 'lazy';
          imageEl.decoding = 'async';

          const mediaLink = imageWrapper.querySelector('.product-addon__media-link');
          if (placeholder instanceof HTMLElement) {
            placeholder.replaceWith(imageEl);
          } else if (mediaLink instanceof HTMLElement) {
            mediaLink.replaceChildren(imageEl);
          } else {
            imageWrapper.prepend(imageEl);
          }
        }
      }

      if (imageEl instanceof HTMLImageElement) {
        // Clear srcset/sizes so Shopify image_tag responsive sources do not override the new src.
        imageEl.removeAttribute('srcset');
        imageEl.removeAttribute('sizes');
        imageEl.src = variantData.imageUrl;
        imageEl.alt = variantData.imageAlt || imageEl.alt;
      }
    }

    const badge = card.querySelector('.product-addon__sold-out-badge');
    if (badge instanceof HTMLElement) {
      badge.hidden = isAvailable;
    }

    card.classList.toggle('product-addon-card--unavailable', !isAvailable);

    const pill = card.querySelector('.product-addon__pill');
    if (pill instanceof HTMLElement) {
      pill.classList.toggle('product-addon__pill--sold-out', !isAvailable);
    }

    if (!isAvailable && checkbox instanceof HTMLInputElement) {
      checkbox.checked = false;
      card.classList.remove('selected');
      if (quantitySection instanceof HTMLElement) {
        quantitySection.classList.remove('visible');
        quantitySection.hidden = true;
      }
      if (this.isModernLayout) {
        this.#syncModernPill(card, false);
      }
    } else if (this.isModernLayout) {
      this.#syncModernPill(card, checkbox instanceof HTMLInputElement && checkbox.checked);
    }
  }

  #setupQuantityControls() {
    const { signal } = this.#abortController ?? {};

    for (const controls of this.querySelectorAll('.product-addon__quantity-controls')) {
      if (!(controls instanceof HTMLElement)) continue;

      const decreaseBtn = controls.querySelector('[data-action="decrease"]');
      const increaseBtn = controls.querySelector('[data-action="increase"]');
      const quantityInput = controls.querySelector('.product-addon__quantity-display');
      const card = controls.closest('.product-addon-card');
      const checkbox = card?.querySelector('.product-addon__checkbox');

      if (!(card instanceof HTMLElement) || isUnavailableCard(card)) continue;
      if (!(checkbox instanceof HTMLInputElement)) continue;

      if (decreaseBtn instanceof HTMLButtonElement && quantityInput instanceof HTMLInputElement) {
        decreaseBtn.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            const parsed = Number.parseInt(quantityInput.value, 10);
            const current = Number.isFinite(parsed) ? parsed : 1;
            if (current <= 0) return;

            const next = current - 1;
            quantityInput.value = String(next);

            if (next === 0) {
              checkbox.checked = false;
              card.classList.remove('selected');
              const qtySection = card.querySelector('.product-addon__quantity-section');
              if (qtySection instanceof HTMLElement) {
                qtySection.classList.remove('visible');
                qtySection.hidden = true;
                quantityInput.value = '1';
              }
              if (this.isModernLayout) {
                this.#syncModernPill(card, false);
              }
            }

            this.updateSelectedVariants();
          },
          { signal }
        );
      }

      if (increaseBtn instanceof HTMLButtonElement && quantityInput instanceof HTMLInputElement) {
        increaseBtn.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            const parsed = Number.parseInt(quantityInput.value, 10);
            const current = Number.isFinite(parsed) ? parsed : 1;
            const parsedMax = Number.parseInt(quantityInput.max, 10);
            const max = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : 10;

            if (current === 0) {
              checkbox.checked = true;
              card.classList.add('selected');
              const qtySection = card.querySelector('.product-addon__quantity-section');
              if (qtySection instanceof HTMLElement) {
                qtySection.classList.add('visible');
                qtySection.hidden = false;
              }
              if (this.isModernLayout) {
                this.#syncModernPill(card, true);
              }
            }

            if (current < max) {
              quantityInput.value = String(current + 1);
              this.updateSelectedVariants();
            }
          },
          { signal }
        );
      }
    }
  }
}

if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddonsElement);
}

export { ProductAddonsElement };
